import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { onAuthChange, isFullyVerified, type User } from '../../lib/auth'

interface AuthContextValue {
  /** The signed-in account. Null for anonymous participants. */
  user: User | null
  /** Firebase uid for anyone, including anonymous joiners. This is the identity Firestore rules check. */
  uid: string | null
  loading: boolean
  verified: boolean
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  uid: null,
  loading: true,
  verified: false,
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authUser, setAuthUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsub = onAuthChange((u) => {
      setAuthUser(u)
      setLoading(false)
    })
    return unsub
  }, [])

  // Anonymous sessions exist only to give joiners an identity for security rules;
  // they must not look like a signed-in account anywhere in the UI.
  const user = authUser && !authUser.isAnonymous ? authUser : null
  const verified = isFullyVerified(user)

  return (
    <AuthContext.Provider value={{ user, uid: authUser?.uid ?? null, loading, verified }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
